import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { 
  Building2, 
  RefreshCw, 
  Wallet, 
  Users, 
  ArrowRight, 
  AlertCircle,
  CheckCircle,
  Clock,
  Banknote,
  TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const InvestecWidget = ({ onNavigate }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/dashboard-widget`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch Investec data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Investec balances synced');
        fetchData();
      } else {
        toast.error('Failed to sync balances');
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card className="col-span-2">
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data?.configured) {
    return (
      <Card className="col-span-2 border-dashed border-2 border-slate-200">
        <CardContent className="p-6 text-center">
          <div className="w-12 h-12 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-3">
            <Building2 className="h-6 w-6 text-slate-400" />
          </div>
          <h3 className="font-semibold text-slate-700">Investec Integration</h3>
          <p className="text-sm text-slate-500 mt-1">Not configured</p>
          <p className="text-xs text-slate-400 mt-2">Add API credentials to enable</p>
        </CardContent>
      </Card>
    );
  }

  if (!data?.connected) {
    return (
      <Card className="col-span-2 border-red-200 bg-red-50">
        <CardContent className="p-6 text-center">
          <div className="w-12 h-12 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-3">
            <AlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <h3 className="font-semibold text-red-700">Connection Error</h3>
          <p className="text-sm text-red-600 mt-1">{data?.error || 'Unable to connect to Investec'}</p>
          <Button 
            size="sm" 
            variant="outline" 
            className="mt-3"
            onClick={handleSync}
            disabled={syncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="col-span-2" data-testid="investec-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <CardTitle className="text-base">Investec Bank</CardTitle>
              <CardDescription className="text-xs">
                {data.accounts_count} accounts • Updated {Math.round(data.cache_age_minutes || 0)}m ago
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8"
              onClick={handleSync}
              disabled={syncing}
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Total Balance */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Total Available</p>
              <p className="text-2xl font-bold text-green-800 mt-1">
                {formatCurrency(data.total_balance)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Wallet className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Main Disbursement Account */}
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Banknote className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-slate-700">Main Disbursement</span>
            </div>
            {data.main_disbursement?.configured ? (
              <span className="text-sm font-bold text-slate-900">
                {formatCurrency(data.main_disbursement.balance)}
              </span>
            ) : (
              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                Not Set
              </Badge>
            )}
          </div>
          {data.main_disbursement?.display_name && (
            <p className="text-xs text-slate-500 mt-1 ml-6">{data.main_disbursement.display_name}</p>
          )}
        </div>

        {/* Investor Accounts Summary */}
        <div className="border rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-slate-700">Investor Accounts</span>
            </div>
            <Badge variant="secondary">{data.investor_accounts?.count || 0} mapped</Badge>
          </div>
          
          {data.investor_accounts?.accounts && data.investor_accounts.accounts.length > 0 ? (
            <div className="space-y-1 mt-2">
              {data.investor_accounts.accounts.slice(0, 3).map((inv, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 truncate max-w-[150px]">{inv.investor_name}</span>
                  <span className="font-medium text-slate-800">{formatCurrency(inv.balance)}</span>
                </div>
              ))}
              {data.investor_accounts.accounts.length > 3 && (
                <p className="text-xs text-slate-400 mt-1">
                  +{data.investor_accounts.accounts.length - 3} more
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400">No investor accounts mapped</p>
          )}
          
          <div className="flex items-center justify-between mt-2 pt-2 border-t">
            <span className="text-xs text-slate-500">Total Investor Funds</span>
            <span className="text-sm font-bold text-blue-700">
              {formatCurrency(data.investor_accounts?.total_balance || 0)}
            </span>
          </div>
        </div>

        {/* Mapping Summary */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t">
          <span>
            <span className="text-green-600 font-medium">{data.mapped_count}</span> mapped • 
            <span className="text-amber-600 font-medium ml-1">{data.unmapped_count}</span> unmapped
          </span>
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-7 text-xs"
            onClick={() => onNavigate && onNavigate('/admin/investec')}
          >
            Manage Accounts
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InvestecWidget;
